import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { MessageIcon } from 'assets/icon/dynamic';
import { imgUrl } from 'utils/url';
import './TimelineSequence.css';

const TICK_STEP = 5;
const PX_PER_SEC = 16;
const ROW_H = 56;
const MIN_LOOP_DISPLAY = 90;

const rowsOf = (b) =>
  Array.isArray(b?.rows) && b.rows.length ? b.rows : [b?.row ?? 0];

const Ruler = ({ displayLoop }) => {
  const ticks = [];
  for (let s = 0; s <= displayLoop; s += TICK_STEP) ticks.push(s);
  return (
    <div className="ts-ruler" style={{ width: `${displayLoop * PX_PER_SEC}px` }}>
      {ticks.map((t) => (
        <div
          key={t}
          className="ts-tick"
          style={{ left: `${t * PX_PER_SEC}px` }}
        >
          <span>{String(t).padStart(2, '0')}</span>
        </div>
      ))}
    </div>
  );
};

const TimelineSequence = ({
  screens,
  screenIds,
  timeline,
  blockDuration,
  blocks,
  setBlocks,
  editId,
  mediaCache,
  selectedMediaIds,
  conflict,
}) => {
  const screenRows = useMemo(() => {
    return (screenIds || []).map((sid) => {
      const tl = (timeline || []).find((t) => t.screenId === sid);
      const screen = (screens || []).find((s) => (s._id || s.id) === sid);
      const lanesData = Array.isArray(tl?.rows) ? tl.rows : [];
      const knownLanes = lanesData.map((r) => r.row);
      const ownLanes = (blocks || [])
        .filter((b) => b.screenId === sid)
        .flatMap((b) => rowsOf(b));
      const lanes = Array.from(
        new Set([...knownLanes, ...ownLanes, 0])
      ).sort((a, b) => a - b);

      const dedupedOther = new Map();
      lanesData.forEach((laneRow) => {
        (laneRow.blocks || []).forEach((b) => {
          if (editId && b.source === 'schedule' && b.sourceId === editId) return;
          const key = `${b.source}:${b.sourceId}:${rowsOf(b).join(',')}:${b.startOffset}`;
          if (!dedupedOther.has(key)) dedupedOther.set(key, b);
        });
      });
      const otherBlocks = Array.from(dedupedOther.values());

      const gapsByLane = {};
      lanesData.forEach((laneRow) => {
        gapsByLane[laneRow.row] = laneRow.gaps || [];
      });

      return {
        screenId: sid,
        screenName: tl?.screenName || screen?.name || 'Screen',
        loopDuration: tl?.loopDuration > 0 ? tl.loopDuration : 0,
        lanes,
        otherBlocks,
        gapsByLane,
      };
    });
  }, [screenIds, timeline, screens, editId, blocks]);

  const [preview, setPreview] = useState({ open: false, seg: null });
  const dragRef = useRef({ kind: null, gidx: null });

  const addOwnAt = (screenId, requestedRows, explicitStart) => {
    setBlocks((prev) => {
      let startOffset;
      if (typeof explicitStart === 'number') {
        startOffset = explicitStart;
      } else {
        const ownInLane = prev.filter(
          (b) =>
            b.screenId === screenId &&
            rowsOf(b).some((r) => requestedRows.includes(r))
        );
        const screenTl = (timeline || []).find((t) => t.screenId === screenId);
        const otherEnd = (screenTl?.rows || [])
          .filter((r) => requestedRows.includes(r.row))
          .flatMap((r) => r.blocks || [])
          .filter(
            (b) =>
              !(editId && b.source === 'schedule' && b.sourceId === editId)
          )
          .reduce(
            (m, b) => Math.max(m, b.startOffset + (b.duration || 0)),
            0
          );
        const ownEnd = ownInLane.reduce(
          (m, b) => Math.max(m, b.startOffset + (b.duration || 0)),
          0
        );
        startOffset = Math.max(ownEnd, otherEnd);
      }
      return [
        ...prev,
        {
          screenId,
          rows: requestedRows,
          startOffset,
          duration: blockDuration || 0,
          mediaIds: selectedMediaIds || [],
        },
      ];
    });
  };

  const updateOwn = (gidx, patch) => {
    setBlocks((prev) =>
      prev.map((b, i) => (i === gidx ? { ...b, ...patch } : b))
    );
  };

  const removeOwn = (gidx) => {
    setBlocks((prev) => prev.filter((_, i) => i !== gidx));
  };

  const conflictHit = (screenId, b) => {
    if (!conflict) return false;
    const inRows = (cRows, bRows) =>
      Array.isArray(cRows) && Array.isArray(bRows)
        ? cRows.some((r) => bRows.includes(r))
        : true;
    if (Array.isArray(conflict.conflicts)) {
      const hit = conflict.conflicts.some(
        (c) =>
          c.screenId === screenId &&
          c.startOffset === b.startOffset &&
          inRows(c.rows, rowsOf(b))
      );
      if (hit) return true;
      const againstHit = conflict.conflicts.some(
        (c) =>
          c.against &&
          b.sourceId &&
          c.against.sourceId === b.sourceId
      );
      if (againstHit) return true;
    }
    if (Array.isArray(conflict.capacityViolations)) {
      if (
        conflict.capacityViolations.some(
          (c) =>
            c.screenId === screenId &&
            c.startOffset === b.startOffset &&
            inRows(c.rows, rowsOf(b))
        )
      )
        return true;
    }
    if (
      conflict.selfOverlap &&
      conflict.selfOverlap.screenId === screenId
    ) {
      const { a, b: cb, rows: cRows } = conflict.selfOverlap;
      if (
        (a?.startOffset === b.startOffset || cb?.startOffset === b.startOffset) &&
        inRows(cRows, rowsOf(b))
      )
        return true;
    }
    return false;
  };

  const onOwnDragStart = (gidx) => (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const grabOffsetPx = e.clientX - rect.left;
    dragRef.current = { kind: 'own', gidx, grabOffsetPx };
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', `own::${gidx}`);
    } catch (_err) {}
  };

  const onTrackDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const SNAP_PX = 12;

  const snapStartOffset = (screenId, lane, rawStart, excludeGidx) => {
    const candidates = [];
    const screenTl = (timeline || []).find((t) => t.screenId === screenId);
    const laneRow = screenTl?.rows?.find((r) => r.row === lane);
    (laneRow?.blocks || []).forEach((b) => {
      candidates.push(b.startOffset);
      candidates.push(b.startOffset + (b.duration || 0));
    });
    (blocks || []).forEach((b, i) => {
      if (i === excludeGidx) return;
      if (b.screenId !== screenId) return;
      if (!rowsOf(b).includes(lane)) return;
      candidates.push(b.startOffset);
      candidates.push(b.startOffset + (b.duration || 0));
    });
    let best = rawStart;
    let bestDist = SNAP_PX / PX_PER_SEC + 0.001;
    candidates.forEach((c) => {
      const d = Math.abs(c - rawStart);
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    });
    return Math.max(0, best);
  };

  const onMultiTrackDrop = (screenId, lanesArr) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top - 24;
    const idx = Math.max(
      0,
      Math.min(lanesArr.length - 1, Math.floor(y / ROW_H))
    );
    const lane = lanesArr[idx];
    const { kind, gidx, grabOffsetPx } = dragRef.current;
    const adjustedX = x - (grabOffsetPx || 0);
    const rawStart = Math.max(0, Math.round(adjustedX / PX_PER_SEC));
    dragRef.current = { kind: null, gidx: null, grabOffsetPx: 0 };
    console.log('[TimelineSequence] drop event', {
      screenId,
      lane,
      laneIdx: idx,
      rawStart,
      pointer: { x, y },
      kind,
      gidx,
    });
    if (kind === 'own' && gidx != null) {
      const cur = (blocks || [])[gidx];
      if (!cur) {
        console.log('[TimelineSequence] drop aborted: no block at gidx', gidx);
        return;
      }
      const span = rowsOf(cur).length;
      const rows = Array.from({ length: span }, (_, i) => lane + i);
      const startOffset = snapStartOffset(screenId, lane, rawStart, gidx);
      console.log('[TimelineSequence] drop resolved', {
        gidx,
        from: {
          screenId: cur.screenId,
          rows: rowsOf(cur),
          startOffset: cur.startOffset,
        },
        to: { screenId, rows, startOffset },
        snapped: startOffset !== rawStart,
      });
      updateOwn(gidx, { screenId, rows, startOffset });
    }
  };

  const onGapClick = (screenId, lane, gap) => () => {
    addOwnAt(screenId, [lane], gap.start);
  };

  const renderOtherBlock = (screenId, b) => {
    const rs = rowsOf(b);
    const top = Math.min(...rs) * ROW_H + 24 + 4;
    const span = rs.length;
    const left = b.startOffset * PX_PER_SEC;
    const width = (b.duration || 0) * PX_PER_SEC;
    const isSlot = b.source === 'slot';
    let cls = 'ts-seg ts-fully-booked';
    if (isSlot) cls += ' ts-slot-other';
    else cls += ' ts-schedule-other';
    if (conflictHit(screenId, b)) cls += ' conflict';
    return (
      <div
        key={`${b.source}:${b.sourceId}`}
        className={cls}
        style={{
          left: `${left}px`,
          width: `${width}px`,
          top: `${top}px`,
          height: `${span * ROW_H - 8}px`,
          position: 'absolute',
        }}
        title={`${b.name} (${b.source})`}
        onClick={(e) => {
          e.stopPropagation();
          setPreview({ open: true, seg: b });
        }}
      >
        <span className="ts-dur">{b.duration}s</span>
        <span className="ts-status">
          <span className="ts-status-text">
            {isSlot ? 'Slot' : 'Booked'}
          </span>
          <i className="feather-bookmark ts-bm" />
        </span>
      </div>
    );
  };

  const renderOwnBlock = (screenId, b, gidx) => {
    const rs = rowsOf(b);
    const top = Math.min(...rs) * ROW_H + 24 + 4;
    const span = rs.length;
    const left = b.startOffset * PX_PER_SEC;
    const width = (b.duration || blockDuration || 0) * PX_PER_SEC;
    let cls = 'ts-seg ts-available selected';
    if (conflictHit(screenId, b)) cls += ' conflict';
    return (
      <div
        key={`own-${gidx}`}
        className={cls}
        style={{
          left: `${left}px`,
          width: `${width}px`,
          top: `${top}px`,
          height: `${span * ROW_H - 8}px`,
          position: 'absolute',
        }}
        draggable
        onDragStart={onOwnDragStart(gidx)}
        title={`Own block @${b.startOffset}s, lanes ${rs.join(',')}`}
        onClick={(e) => {
          e.stopPropagation();
          setPreview({ open: true, seg: { ...b, source: 'own' } });
        }}
      >
        <span className="ts-dur">{b.duration || blockDuration}s</span>
        <span className="ts-status">
          <span className="ts-status-text">Selected</span>
          <span className="ts-toggle on" draggable={false}>
            <i className="feather-check" />
          </span>
          <button
            type="button"
            className="ts-remove"
            onClick={(e) => {
              e.stopPropagation();
              removeOwn(gidx);
            }}
            draggable={false}
            title="Remove block"
          >
            <i className="feather-x" />
          </button>
        </span>
      </div>
    );
  };

  return (
    <div className="ts-wrap">
      <h3 className="section-title">Timeline Sequence</h3>

      <div className="ts-rows">
        {screenRows.map((row) => {
          const loop = row.loopDuration || 0;
          const ownInScreen = (blocks || [])
            .map((b, i) => ({ b, gidx: i }))
            .filter(({ b }) => b.screenId === row.screenId);
          const allEnds = [
            ...row.otherBlocks.map((b) => b.startOffset + (b.duration || 0)),
            ...ownInScreen.map(
              ({ b }) => b.startOffset + (b.duration || blockDuration || 0)
            ),
          ];
          const contentEnd = allEnds.length ? Math.max(...allEnds) : 0;
          const displayLoop = Math.max(loop, contentEnd, MIN_LOOP_DISPLAY);
          const trackWidthPx = displayLoop * PX_PER_SEC;
          const lanes = row.lanes.length ? row.lanes : [0];
          const trackHeight = lanes.length * ROW_H + 24;

          return (
            <div className="ts-row" key={row.screenId}>
              <div className="ts-row-side">
                <MessageIcon size={28} color="#64D5D2" />
                <div className="ts-row-name">
                  {row.screenName.toUpperCase()}
                </div>
              </div>

              <div className="ts-row-main">
                <div className="ts-template-name">
                  Loop {loop || '—'}s · {lanes.length} lane{lanes.length > 1 ? 's' : ''}
                </div>
                <div className="ts-track-shell">
                  <div className="ts-scroll">
                    <div
                      className="ts-scroll-inner"
                      style={{ width: `${trackWidthPx}px` }}
                    >
                      <Ruler displayLoop={displayLoop} />
                      <div
                        className="ts-multi-track"
                        onDragOver={onTrackDragOver}
                        onDrop={onMultiTrackDrop(row.screenId, lanes)}
                        style={{
                          position: 'relative',
                          width: `${trackWidthPx}px`,
                          height: `${trackHeight}px`,
                        }}
                      >
                        {lanes.map((lane, i) => (
                          <div
                            key={`lane-${lane}`}
                            className="ts-lane"
                            style={{
                              position: 'absolute',
                              top: `${i * ROW_H + 24}px`,
                              left: 0,
                              width: `${trackWidthPx}px`,
                              height: `${ROW_H}px`,
                              background: '#F8FAFC',
                            }}
                          >
                            <span className="ts-lane-label">lane {lane}</span>
                            {(row.gapsByLane[lane] || []).map((g, gi) => {
                              const gw = (g.end - g.start) * PX_PER_SEC;
                              if (gw <= 0) return null;
                              return (
                                <div
                                  key={`gap-${lane}-${gi}`}
                                  className="ts-gap"
                                  style={{
                                    position: 'absolute',
                                    left: `${g.start * PX_PER_SEC}px`,
                                    top: 4,
                                    width: `${gw}px`,
                                    height: `${ROW_H - 12}px`,
                                  }}
                                  onClick={onGapClick(row.screenId, lane, g)}
                                  title={`Add own block at ${g.start}s`}
                                />
                              );
                            })}
                          </div>
                        ))}

                        {row.otherBlocks.map((b) =>
                          renderOtherBlock(row.screenId, b)
                        )}
                        {ownInScreen.map(({ b, gidx }) =>
                          renderOwnBlock(row.screenId, b, gidx)
                        )}

                        {(() => {
                          const lane = lanes[0];
                          if (lane === undefined) return null;
                          const ownEnd = ownInScreen.reduce((m, { b }) => {
                            if (!rowsOf(b).includes(lane)) return m;
                            return Math.max(m, b.startOffset + (b.duration || 0));
                          }, 0);
                          const otherEnd = row.otherBlocks.reduce((m, b) => {
                            if (!rowsOf(b).includes(lane)) return m;
                            return Math.max(m, b.startOffset + (b.duration || 0));
                          }, 0);
                          const endSec = Math.max(ownEnd, otherEnd);
                          const widthSec = blockDuration || 10;
                          return (
                            <div
                              key={`new-${lane}`}
                              className="ts-seg ts-available"
                              style={{
                                position: 'absolute',
                                left: `${endSec * PX_PER_SEC}px`,
                                top: `${24 + 4}px`,
                                width: `${widthSec * PX_PER_SEC}px`,
                                height: `${ROW_H - 8}px`,
                                cursor: 'pointer',
                              }}
                              onClick={() => addOwnAt(row.screenId, [lane])}
                              title="Add new block"
                            >
                              <span className="ts-dur">{widthSec}s</span>
                              <span className="ts-status">
                                <span className="ts-status-text">Available</span>
                                <span className="ts-toggle" />
                              </span>
                            </div>
                          );
                        })()}

                        {loop > 0 && (
                          <div
                            className="ts-loop-end"
                            style={{
                              position: 'absolute',
                              left: `${loop * PX_PER_SEC}px`,
                              top: 24,
                              bottom: 0,
                              width: 2,
                              background: '#EF4444',
                              opacity: 0.5,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={preview.open}
        onCancel={() => setPreview({ open: false, seg: null })}
        footer={null}
        title={
          preview.seg
            ? preview.seg.source === 'own'
              ? 'Preview — New Block'
              : `Preview — ${preview.seg.name || 'Booking'}`
            : 'Preview'
        }
        width={560}
      >
        {preview.seg && (() => {
          const ids =
            preview.seg.source === 'own'
              ? selectedMediaIds || []
              : preview.seg.mediaIds || [];
          if (!ids.length) {
            return <div className="ts-preview-empty">No media info available.</div>;
          }
          return (
            <div className="ts-preview-list">
              {ids.map((mid) => {
                const id = typeof mid === 'string' ? mid : mid?._id || mid?.id;
                const m =
                  (mediaCache && mediaCache[id]) ||
                  (typeof mid === 'object' ? mid : null);
                if (!m) return null;
                const isVideo =
                  m.fileType && m.fileType.split('/')[0] === 'video';
                const src = m.logo
                  ? m.logo.startsWith('http')
                    ? m.logo
                    : `${imgUrl}/media/${m.logo}`
                  : null;
                return (
                  <div key={id} className="ts-preview-row">
                    {src && !isVideo && (
                      <img src={src} alt={m.name} className="ts-preview-img" />
                    )}
                    {src && isVideo && (
                      <video src={src} controls className="ts-preview-img" />
                    )}
                    <div className="ts-preview-meta">
                      <div className="ts-preview-name">{m.name}</div>
                      {m.duration != null && (
                        <div className="ts-preview-dur">{m.duration}s</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default TimelineSequence;
